import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { db, database, auth, messaging } from '../lib/firebase';
import { getWeekInfo, normalizeWeek } from '../lib/weekHelper';
import { confirmPasswordReset, sendPasswordResetEmail } from "firebase/auth";
import { getToken } from "firebase/messaging";
import { clearIndexedDbPersistence, terminate } from 'firebase/firestore';
import { ref, get as getSnapshot, set, update } from 'firebase/database';

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential
} from 'firebase/auth';
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  updateDoc,
  doc,
  deleteDoc,
  setDoc,
  getDoc,
  Timestamp,
  arrayRemove,
  arrayUnion,
  getDocFromCache,
  getDocsFromCache
} from 'firebase/firestore';
import { formatDate } from '@/lib/dateFormatter';
import { replace } from 'lodash';

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  location?: string;
  bio?: string;
  avatar?: string;
  phone?: string;
  termsAccepted?: true | false;
  timeZone?: string;
  wooCommerceId?: string | number | null;
  paymentInfo?: {
    cardLast4?: string;
    brand: string;
  }
  stripeSessions?: StripeSession[];
  stripeCustomerId?: string;
  activeSubscriber?: boolean;
  subscription?: 'free' | 'pro' | null;
  lastSubscriptionDate?: string | Date;
  referredBy?: string;
  couponId?: string;
  FCMTokens?: any[];
  createdAt?: any;
  verified?: boolean;
  verifiedAt?: any;
  completedOnboarding?: boolean;
  estimatedCosts?: {
    [recipeId: string]: {
      amount?: number;
      currency?: string;
    };
  };
}

export interface StripeSession {
  id: string;
  sessionId: string | number;
  created: string;
  cardLast4: string;
  customerId: string;
  subscriptionId: string,
  amount: number,
  status: string,
}

export interface Recipe {
  id: string;
  title: string;
  image: string;
  prepTime: string;
  cookTime: string;
  servings: number;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  category: string[];
  description?: string;
  calories?: number | string;
  isFavorite: boolean;
  favorites?: string[];
  ingredients?: string[];
  instructions?: string[];
  tags?: string[];
  meals?: string[];
  notes?: string;
  userId?: string;
  spiceLevel?: string;
  isVariation?: boolean;
  estimatedCost?: number;
  estimatedCosts?: {
    [userId: string]: {
      amount?: number;
      currency?: string;
    };
  };
}

export interface Meal {
  id: string;
  title: string;
  type: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  image?: string;
  calories: number;
  recipeId?: string;
  recipe?: Recipe;
  difficulty?: string;
  time?: string;
}

export interface DayPlan {
  date: Date | Timestamp | any;
  meals: {
    breakfast?: Meal[] | Meal | null;
    lunch?: Meal[] | Meal | null;
    dinner?: Meal[] | Meal | null;
    snacks?: Meal[];
  };
}

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'marketing';
  date: Date;
  read: boolean;
  link?: string;
  image?: string;
}

export interface Plan {
  id: 'free' | 'pro';
  name: string;
  price: number;
  period: 'month' | 'year';
  description: string;
  features: string[];
  notIncluded?: string[];
}

export interface UserPreferences {
  theme: 'light' | 'dark';
  measurementSystem: 'metric' | 'imperial';
  notifications: {
    email: boolean;
    newRecipes: boolean;
    weeklyDigest: boolean;
    specialOffers: boolean;
  };
  mealTimes?: Array<{
    type: 'breakfast' | 'lunch' | 'dinner';
    time: string;
  }> | any[];
}

interface Location {
  country: string;
  currency: string;
  currencySymbol: string;
}

export interface WeeklyPlans {
  [weekId: string]: DayPlan[];
}

export interface ShoppingList {
  [weekId: string]: any;
}

export interface ResetMessage {
  type: "error" | "success";
  message: string;
}

export interface Transaction {
  id: string;
  date: Date | string;
  amount: number;
  status: string;
  cardLast4: string;
  currentPeriodStart?: Date | string;
  currentPeriodEnd?: Date | string;
  defaultPaymentMethod?: {
    brand: string;
    cardLast4: string;
    expMonth: number;
    expYear: number;
  }
}

interface StoreState {
  addRecipeVariation: any;
  user: User | null;
  recipes: Recipe[];
  savedRecipes: string[];
  notifications: Notification[];
  userPreferences: UserPreferences | null;
  subscription: 'free' | 'pro' | null;
  isLoading: boolean;
  showSignUp: boolean;
  weeklyPlan: WeeklyPlans;
  shoppingList: any;
  location: Location;
  plans: Plan[];
  mealTemplate: Recipe[];
  mealPriority: string[];
  mealPriorities: Record<string, boolean>;
  mealsPerWeek: number;
  mealTime: any;
  appLogo: string;
  subscriptionAmount: number;
  subscriptionHistory: {
    amount: number;
    nextDueDate: string;
    subscriptions: Transaction[],
    defaultPaymentMethod?: {
      brand?: string;
      cardLast4?: string;
      expMonth?: number;
      expYear?: number;
    }
  } | undefined;
  generatingWeeklyPlan: boolean;
  showOnboarding: boolean;
  lastFetch: any;

  login: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, firstName: string, lastName: string, referredBy?: string, termsAccepted?: boolean, couponId?: string, campaignId?: string, platform?: string) => Promise<void>;
  loginWithGoogle: (referredBy?: string, couponId?: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUserPassword: (currentPassword: string, newPassword: string) => Promise<void>;
  addRecipe: (recipe: Recipe) => Promise<void>;
  updateRecipe: (id: string, recipe: Partial<Recipe>) => Promise<void>;
  deleteRecipe: (id: string) => Promise<void>;
  toggleFavorite: (id: string | string[], options?: any) => Promise<void>;
  addNotification: (notification: Omit<Notification, 'id' | 'date' | 'read'>) => void;
  markNotificationAsRead: (id: string) => void;
  clearNotifications: () => void;
  updatePreferences: (prefs: Partial<UserPreferences>) => Promise<void>;
  updateLocation: (location: Location) => void;
  updateProfile: (user: User) => Promise<void>;
  setShowSignUp: (show: boolean) => void;
  updateSubscription: (subscription: 'free' | 'pro' | null) => void;
  cancelSubscription: () => Promise<void>;
  addMealToPlan: (date: Date, mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack', weekId: string, recipe: Meal[]) => Promise<void>;
  removeMealFromPlan: (date: Date, mealType: string, weekId: string, mealId?: string) => Promise<void>;
  estimateRecipeCost: (recipeId: string, currency: string, amount: number) => void;
  toggleSavedRecipe: (recipeId: string) => void;
  setPlans: (plans: Plan[]) => void;
  updateMealTemplate: (mealTemplate: Recipe[], mealPriority: string[], mealsPerWeek: number, mealPriorities: any) => void;
  updateWeeklyPlan: (weeklyPlan: WeeklyPlans) => void;
  fetchWeeklyPlan: () => void;
  generateWeeklyPlan: (mergeType?: 0 | 1 | 2) => void;
  updateLoadingStatus: (isLoading: boolean) => void;
  resetPassword: (email: string, password?: string, oobCode?: string) => Promise<ResetMessage>;
  fetchSubscriptionHistory: (stripeCustomerId: string) => Promise<void>;
  fetchStripeCustomer: (email: string, fullname: string) => Promise<any>;
  createStripeCustomer: (email: string, fullname: string) => Promise<any>;
  fetchRecipeById: (recipeId: string) => any;
  fetchRecipes: () => void;
  groupIngredients: (ingredients: string[]) => any;
  acceptTerms: () => void;
  requestNotificationPermission: () => void;
  fetchNotifications: () => void;
  getSubscriptionAmount: (couponId?: string) => void;
  loadPrerequisites: () => void;
  updateShowOnboarding: (status: boolean) => void;
  updateUserData: () => void;
  resendOTP: () => Promise<boolean>;
  verifyOTP: (otp: string) => void;
  setWeeklyPlan: (plan: any) => void;
  clearPlan: () => void;
  updateMealTime: (mealTime: any) => void;
  fetchMealTemplate: () => void;
}

const googleProvider = new GoogleAuthProvider();

if (typeof window !== 'undefined') {
  document.documentElement.classList.add('dark');
}

const completeSignUp = async (uid: string, email: string, firstName: string, lastName: string, token: string, referredBy?: string, couponId?: string, campaignId?: string, platform?: string) => {
  const baseUrl = import.meta.env.VITE_API_URL || 'https://app.ezyvibes.com';
  let payload = { uid, email, firstName, lastName, idToken: token, referredBy, couponId, campaignId, platform };
  payload = JSON.parse(JSON.stringify(payload));
  const response = await fetch(`${baseUrl}/api/complete-signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  })

  const data = await response.json()
  console.log('Complete Sign Up Response:', data)
  return data
}

const validateToken = async (token: string, uid: string) => {
  const baseUrl = import.meta.env.VITE_API_URL || 'https://app.ezyvibes.com';
  const response = await fetch(`${baseUrl}/api/validate-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ uid, token })
  })

  const data = await response.json()
  console.log('Verify Token Response:', data)
  return data
}

const fetchRecipesFromCache = async () => {
  const baseUrl = import.meta.env.VITE_API_URL || 'https://app.ezyvibes.com';
  const response = await fetch(`${baseUrl}/api/redisService`);
  const data = await response.json();
  return data;
}

const updateRecipesInCache = async (recipes: Recipe[]) => {
  const path = 'recipes'
  const recipeMap: Record<string, any> = {};

  recipes.forEach(recipe => {
    const cleaned = JSON.parse(JSON.stringify(recipe)); // strips undefined
    recipeMap[recipe.id] = cleaned;
  });

  await set(ref(database, path), recipeMap);
  // await set(ref(database, path), recipes);
}

const logAuthError = async (process: string, payload: any) => {
  const baseUrl = import.meta.env.VITE_API_URL || 'https://app.ezyvibes.com';
  const response = await fetch(`${baseUrl}/api/log-auth-error`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ process, payload })
  })

  const data = await response.json()
  console.log('Error Logging Auth Data Response:', data)
  return data
}

const dummyNotifications: Notification[] = []

export const useStore = create<StoreState>()(
  persist(
    (set, get) => ({
      user: null,
      recipes: [],
      savedRecipes: [],
      notifications: dummyNotifications,
      userPreferences: null,
      subscription: null,
      isLoading: false,
      showSignUp: false,
      weeklyPlan: {},
      addRecipeVariation: null,
      showOnboarding: false,
      plans: [],
      location: { country: 'US', currency: 'USD', currencySymbol: '$' },
      mealTemplate: [],
      mealPriorities: {
        breakfast: true,
        lunch: true,
        dinner: true
      },
      mealTime: [{
        type: "breakfast",
        time: "07:00",
      }, {
        type: "lunch",
        time: "12:00"
      }, {
        type: "dinner",
        time: "18:00"
      }],
      mealPriority: ["breakfast", "lunch", "dinner"],
      mealsPerWeek: 7,
      appLogo: "https://res.cloudinary.com/drive-cloud/image/upload/v1756090835/Asset_1-removebg-preview_cbbaej.png",
      subscriptionHistory: undefined,
      shoppingList: [],
      generatingWeeklyPlan: false,
      subscriptionAmount: 10.99,
      lastFetch: undefined,

      setWeeklyPlan: (plan) => set({ weeklyPlan: plan }),

      fetchNotifications: async () => {
        const userId = get().user?.id;
        if (!userId) {
          throw new Error('User ID is undefined');
        }
        const notificationsRef = doc(db, "notifications", userId);
        const notificationsDoc = await getDoc(notificationsRef);
        let notifications = notificationsDoc.data()?.notifications as Notification[];
        if (notificationsDoc.data()) {
          set({
            notifications
          })
        }
        let lastFetch = get().lastFetch ? new Date(get().lastFetch) : null;

        // Fix the typo and properly subtract 24 hours from current time
        let DAY_AGO = new Date(Date.now() - 24 * 60 * 60 * 1000); // 24 hours in milliseconds

        if (lastFetch && lastFetch < DAY_AGO) {
          await get().loadPrerequisites();
        }
      },

      getSubscriptionAmount: async (couponId?: string) => {
        const baseUrl = import.meta.env.VITE_API_URL || 'https://app.ezyvibes.com';
        const response = await fetch(`${baseUrl}/api/get-amount`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ couponId })
        })
        let data = await response.json()
        console.log('Stripe Response:', data)
        set({ subscriptionAmount: data.amountToPay });
      },

      fetchStripeCustomer: async (email: string, fullname: string) => {
        const baseUrl = import.meta.env.VITE_API_URL || 'https://app.ezyvibes.com';
        const response = await fetch(`${baseUrl}/api/get-stripe-customer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        })

        let data = await response.json()
        if (!data.data[0]?.id) {
          data = await get().createStripeCustomer(email, fullname)
        } else {
          data = data.data[0];
        }
        console.log('Stripe Response:', data)
        return data;
      },

      createStripeCustomer: async (email: string, fullname: string) => {
        const baseUrl = import.meta.env.VITE_API_URL || 'https://app.ezyvibes.com';
        const response = await fetch(`${baseUrl}/api/create-stripe-customer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, fullname })
        });

        const data = await response.json();
        console.log('Stripe Response:', data)
        return data;
      },

      clearPlan: async () => {
        set({ isLoading: true });
        try {
          const user = get().user;
          const userId = user?.id;

          if (!userId) {
            throw new Error('User ID is undefined');
          }

          const planRef = doc(db, 'weeklyPlans', userId);

          // console.log("Deleting plan... ", userId);
          await deleteDoc(planRef);
        } catch (err) {
          console.error(`Error clearing meal plans ${err}`);
        }
        try {
          get().loadPrerequisites();
        } catch (err) {
          console.error(`Error loading prerequisites after clearing meal plans ${err}`);
        }
        set({ weeklyPlan: {} });
        set({ isLoading: false });
      },

      fetchSubscriptionHistory: async (stripeCustomerId: any) => {
        console.log("Fetching subscription history...");
        set({ isLoading: true });
        try {
          const baseUrl = import.meta.env.VITE_API_URL || 'https://app.ezyvibes.com';
          if (!stripeCustomerId) {
            console.error("Stripe customer ID is undefined");
            set({ isLoading: false });
            return;
          }
          const response = await fetch(`${baseUrl}/api/get-stripe-transactions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ stripeCustomerId })
          })

          const subscriptionHistory = await response.json()
          console.log('Stripe Response:', subscriptionHistory)
          set({ subscriptionHistory });
          const userId = get().user?.id;
          if (userId) {
            await setDoc(doc(db, 'users', userId), { subscriptionHistory, lastTransactionFetch: new Date().toISOString() }, { merge: true });
          }
        } catch (err) {
          console.log(err);
        }
        set({ isLoading: false });
      },
      requestNotificationPermission: async () => {
        try {
          const permission = await Notification.requestPermission();
          if (permission === 'granted') {
            const token = await getToken(messaging, {
              vapidKey: "BDxhl-K-mQ6AeN5-DkRG6Xq9CsWSe14X5-aSeh034X2Za6kwaEJy8CqyNfGwXrNIwrySnzR58mm2lKZjGmhFDJg", // Replace with your actual key
            });
            console.log('FCM Token:', token);
            localStorage.setItem("browserFCMToken", token);
            let user = get().user;

            if (!user?.id) {
              throw new Error('User ID is undefined');
            }
            set({
              user:
              {
                ...user,
                id: user?.id ?? "", // Ensure id is always a string
                email: user?.email ?? "", // Ensure email is always a string
                FCMTokens: Array.isArray(user.FCMTokens) ? [...user.FCMTokens, token] : [user.FCMTokens]
              }
            });
            const userRef = doc(db, 'users', user.id);
            await setDoc(userRef, {
              FCMTokens: arrayUnion(token)
            }, { merge: true });

          } else {
            console.warn('Notification permission not granted.');
          }
        } catch (err) {
          console.error('FCM permission error:', err);
        }
      },
      updateShowOnboarding: async (status: boolean) => {
        const user = get().user;
        if (!user || !user.id) return;
        set({ user: { ...user, id: user.id, email: user.email ?? "", completedOnboarding: !status } });
        await setDoc(doc(db, 'users', user.id), { completedOnboarding: !status }, { merge: true });
      },
      resetPassword: async (email: string, password?: string, oobCode?: string) => {
        set({ isLoading: true });
        let response: ResetMessage
        try {
          if (password && oobCode) {
            await confirmPasswordReset(auth, oobCode, password);
            console.log("Password update successful. ");
            response = { type: "success", message: "Password update successful, proceed to login." }
          } else {
            await sendPasswordResetEmail(auth, email);
            console.log("Password reset email sent! Check your inbox.");
            response = { type: "success", message: "Password reset email sent! if you're a registered user check your inbox." }
          }
        } catch (error) {
          console.log(`Error: ${error}`);
          let process = "RESET_PASSWORD "
          await logAuthError(process, error);
          response = { type: "error", message: `Error: Password reset failed, check logs for error` };
        }
        set({ isLoading: false })
        return response;
      },
      updateLoadingStatus: async (isLoading: boolean) => {
        set({ isLoading });
      },
      login: async (email: string, password: string) => {
        set({ isLoading: true });

        try {
          const userCredential = await signInWithEmailAndPassword(auth, email, password);
          const { uid } = userCredential.user;
          const token = await userCredential.user.getIdToken(); // This is the token
          let user = await validateToken(token, uid);
          if (user) {
            set({ user });
            if (user.subscriptionHistory) {
              set({ subscriptionHistory: user.subscriptionHistory });
            }
          } else {
            throw "validation error!";
          }
          await get().loadPrerequisites();

          try {
            const userPrefRef = doc(db, 'userPreferences', uid);
            const userPrefSnap = await getDoc(userPrefRef);
            const userPrefData = userPrefSnap.data();
            console.log("Preferences captured during login: ", userPrefData)
            let userPreferences: UserPreferences = userPrefData as UserPreferences;
            if (userPreferences?.theme === "light") {
              set({ appLogo: "https://res.cloudinary.com/drive-cloud/image/upload/v1756090835/Asset_1-removebg-preview_cbbaej.png" });
            } else {
              set({ appLogo: "https://res.cloudinary.com/drive-cloud/image/upload/v1756090835/Asset_1-removebg-preview_cbbaej.png" });
            }
            set({ userPreferences });
          } catch (error) {
            console.error('Error setting user preferences on login:', error);
          }

          get().addNotification({
            title: 'Welcome Back!',
            message: `Great to see you again, ${user?.firstName || 'there'}!`,
            type: 'success'
          });
        } catch (error) {
          console.error('Login error:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
      loadPrerequisites: async () => {
        set({ isLoading: true });
        try {
          await get().fetchRecipes();
          await get().getSubscriptionAmount();
          await get().fetchWeeklyPlan();
          set({ lastFetch: new Date() });
          console.log("Prerequisites loaded successfully");
        } catch (err) {
          console.log("An error occurred fetching required data")
        }
        set({ isLoading: false });
      },
      loginWithGoogle: async (referredBy?: string, couponId?: string) => {
        set({ isLoading: true });
        let notificationTitle = 'Welcome to Ezyvibes!';
        let notificationMsg = 'Start your ezyvibes journey by exploring our recipe collection.';
        try {
          googleProvider.setCustomParameters({
            prompt: 'select_account'
          });
          const result = await signInWithPopup(auth, googleProvider);
          if (!result) {
            set({ isLoading: false });
            throw "validation error!";
          }
          const uid = result.user.uid;
          await get().getSubscriptionAmount(couponId);
          if (uid) {
            const userRef = doc(db, 'users', uid);
            const userDoc = await getDoc(userRef);
            if (!userDoc.exists()) {
              const [firstName, lastName] = result.user.displayName?.split(' ') || ['', ''];
              const email = result.user.email;
              const createdAt = new Date().toISOString();
              await setDoc(userRef, { id: uid, firstName, lastName, email, createdAt, referredBy, couponId, verified: true, verifiedAt: createdAt }, { merge: true });
            } else {
              const userData = userDoc.data();
              notificationTitle = 'Welcome Back!';
              notificationMsg = `Great to see you again, ${userData.firstName}!`
            }
          }
          // Get ID Token (JWT)
          const idToken = await result.user.getIdToken();
          let user = await validateToken(idToken, uid);
          if (user) {
            set({ user });
            if (user.subscriptionHistory) {
              set({ subscriptionHistory: user.subscriptionHistory });
            }
            get().addNotification({
              title: notificationTitle,
              message: notificationMsg,
              type: 'success'
            });
          } else {
            throw "validation error!";
          }
        } catch (error) {
          console.error('Google login error:', error);
          throw error;
        } finally {
          set({ isLoading: false });
          await get().loadPrerequisites();
        }
      },

      signUp: async (email: string, password: string, firstName: string, lastName: string, referredBy?: string, termsAccepted?: boolean, couponId?: string) => {
        set({ isLoading: true });
        try {
          const userCredential = await createUserWithEmailAndPassword(auth, email, password);
          const { uid } = userCredential.user;
          const idToken = await userCredential.user.getIdToken();

          let user: any;
          try {
            user = await completeSignUp(uid, email, firstName, lastName, idToken, referredBy, couponId);
            await get().getSubscriptionAmount(couponId);
            set({ user });
            if (user.subscriptionHistory) {
              set({ subscriptionHistory: user.subscriptionHistory });
            }
          } catch (err) {
            console.error('Error completing user creation:', err);
            throw err;
          }

          await get().loadPrerequisites();

          get().addNotification({
            title: 'Welcome to Ezyvibes!',
            message: 'Start your ezyvibes journey by exploring our recipe collection.',
            type: 'success'
          });
        } catch (error) {
          console.error('Signup error:', error);
          let process = "SIGN_UP";
          let payload = {
            email,
            firstName,
            lastName,
            referredBy,
            termsAccepted,
            error,
            couponId
          }
          payload = JSON.parse(JSON.stringify(payload));
          await logAuthError(process, payload);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updateUserPassword: async (currentPassword: string, newPassword: string) => {
        const user = auth.currentUser;
        if (!user || !user.email) throw new Error('No user logged in');

        try {
          const credential = EmailAuthProvider.credential(user.email, currentPassword);
          await reauthenticateWithCredential(user, credential);
          await updatePassword(user, newPassword);
        } catch (error: any) {
          if (error.code === 'auth/wrong-password') {
            throw new Error('Current password is incorrect');
          }
          throw new Error('Failed to update password');
        }
      },

      logout: async () => {
        try {
          await signOut(auth);
          set({ user: null, isLoading: false });
          localStorage.removeItem('ezyvibes-hub-storage');
        } catch (error) {
          console.error('Logout error:', error);
          throw error;
        }
      },

      fetchRecipes: async () => {
        const recipesRef = collection(db, 'recipes');
        let querySnapshot;
        let lastFetch = get().lastFetch ? new Date(get().lastFetch) : null;

        // Fix the typo and properly subtract 24 hours from current time
        let DAY_AGO = new Date(Date.now() - 24 * 60 * 60 * 1000); // 24 hours in milliseconds

        if (lastFetch && lastFetch >= DAY_AGO) {
          try {
            querySnapshot = await getDocsFromCache(recipesRef);
            console.log("Fetching from cache", lastFetch, DAY_AGO);
          } catch (err) {
            querySnapshot = await getDocs(recipesRef);
            console.log("Fetching from server because cache is stale or missing");
          }
        } else {
          querySnapshot = await getDocs(recipesRef);
          console.log("Fetching from server because cache is stale or missing");
        }

        if (querySnapshot.empty || querySnapshot.docs.length === 0) {
          querySnapshot = await getDocs(recipesRef);
          console.log("Fetching from server...");
        }

        const fetchedRecipes = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            isFavorite: data.favorites?.includes(get().user?.id),
          };
        }) as Recipe[];
        useStore.setState({ recipes: fetchedRecipes });
        try {
          updateRecipesInCache(fetchedRecipes);
        } catch (err) {
          console.log("Error updating recipes in cache");
        }
      },

      addRecipe: async (recipe: Recipe) => {
        try {
          const docRef = await addDoc(collection(db, 'recipes'), {
            ...recipe,
            userId: get().user?.id
          });

          set(state => ({
            recipes: [...state.recipes, { ...recipe, id: docRef.id }]
          }));

          get().addNotification({
            title: 'Recipe Added',
            message: `${recipe.title} has been added to your cookbook`,
            type: 'success'
          });
        } catch (error) {
          console.error('Error adding recipe:', error);
          throw error;
        }
      },

      groupIngredients: async (ingredients: string[]) => {
        const baseUrl = import.meta.env.VITE_API_URL || 'https://app.ezyvibes.com';

        const response = await fetch(`${baseUrl}/api/group-ingredients`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ ingredients }),
        });
        const data = await response.json();

        console.log("Grouped ingredients: ", data);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(`HTTP error! status: ${response.status}. ${errorData.message || ''}`);
        }
        return data;
      },

      updateRecipe: async (id: string, recipeData: Partial<Recipe>) => {
        try {
          // const recipeRef = doc(db, 'recipes', id.toString());
          // await updateDoc(recipeRef, recipeData);

          set(state => ({
            recipes: state.recipes.map(recipe =>
              recipe.id === id ? { ...recipe, ...recipeData } : recipe
            )
          }));

          get().addNotification({
            title: 'Recipe Updated',
            message: 'Your recipe has been updated successfully',
            type: 'success'
          });
        } catch (error) {
          console.error('Error updating recipe:', error);
          throw error;
        }
      },

      fetchWeeklyPlan: async () => {
        const userId = get().user?.id;
        if (!userId) {
          throw new Error('User ID is undefined');
        }

        const planRef = doc(db, 'weeklyPlans', userId);
        const planSnap = await getDoc(planRef);

        if (planSnap.exists()) {
          const planData = planSnap.data();
          set({ weeklyPlan: planData });
        } else {
          const days = Array.from({ length: 7 }, (_, i) => {
            const date = new Date();
            date.setDate(date.getDate() - date.getDay() + i + 1);
            return {
              date,
              meals: {
                breakfast: undefined,
                lunch: undefined,
                dinner: undefined,
                snacks: []
              }
            };
          });
          let weekId = getWeekInfo()
          set({ weeklyPlan: { [weekId]: days } });
        }
      },

      generateWeeklyPlan: async (mergeType?: number) => {
        const userId = get().user?.id;
        if (!userId) {
          throw new Error('User ID is undefined');
        }

        try {
          if (!auth.currentUser) {
            throw new Error("No user is currently authenticated");
          }

          set({ generatingWeeklyPlan: true });
          const idToken = await auth.currentUser.getIdToken(true);

          const baseUrl = import.meta.env.VITE_API_URL || 'https://app.ezyvibes.com';

          const response = await fetch(`${baseUrl}/api/generate-weekly-plan`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ userId, idToken, mergeType }),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            get().addNotification({
              title: 'Weekly Plan Generation Failed',
              message: 'Unable to generate your weekly meal plan. Please try again later.',
              type: 'error'
            });
            set({ generatingWeeklyPlan: false });
            throw new Error(`HTTP error! status: ${response.status}. ${errorData.message || ''}`);
          }

          const data = await response.json();
          get().addNotification({
            title: 'Weekly Plan Generated',
            message: 'Your weekly plan has been successfully created and is now ready for review. Please check your dashboard to view the details and prepare for the upcoming week.',
            type: 'info'
          });
          set({ generatingWeeklyPlan: false });
          await get().fetchWeeklyPlan();
          return data;
        } catch (error) {
          console.error("Error generating weekly plan:", error);
          get().addNotification({
            title: 'Weekly Plan Generation Failed',
            message: 'Unable to generate your weekly meal plan. Please try again later.',
            type: 'error'
          });
          set({ generatingWeeklyPlan: false });
          throw error;
        }
      },

      deleteRecipe: async (id: string) => {
        try {
          await deleteDoc(doc(db, 'recipes', id));

          set(state => ({
            recipes: state.recipes.filter(recipe => recipe.id !== id)
          }));

          get().addNotification({
            title: 'Recipe Deleted',
            message: 'Recipe has been removed from your cookbook',
            type: 'info'
          });
        } catch (error) {
          console.error('Error deleting recipe:', error);
          throw error;
        }
      },

      toggleFavorite: async (
        ids: string | string[],
        options?: { replace?: boolean }
      ) => {
        const { replace = false } = options || {};
        const userId = get().user?.id;
        if (!userId) return;

        // normalize to array
        const idArray = Array.isArray(ids) ? ids : [ids];
        let favorites = [];

        try {
          if (replace) {
            // --- Replace mode: set ONLY the provided ids as favorites ---
            const allRecipes = get().recipes;

            // Collect all updates first
            const updatedRecipes = await Promise.all(
              allRecipes.map(async (recipe) => {
                const recipeRef = ref(database, `recipes/${recipe.id}/favorites`);
                const snapshot = await getSnapshot(recipeRef);
                const currentFavorites: string[] = snapshot.exists()
                  ? snapshot.val()
                  : [];

                const shouldBeFav = idArray.includes(recipe.id);
                const isFavNow = currentFavorites.includes(userId);

                // Remote updates only if needed
                if (shouldBeFav && !isFavNow) {
                  await update(ref(database, `recipes/${recipe.id}`), {
                    favorites: [...currentFavorites, userId],
                  });
                } else if (!shouldBeFav && isFavNow) {
                  await update(ref(database, `recipes/${recipe.id}`), {
                    favorites: currentFavorites.filter((uid) => uid !== userId),
                  });
                }

                // Return a new recipe object with the correct local flag
                return { ...recipe, isFavorite: shouldBeFav };
              })
            );

            // ✅ Single Zustand state update
            set((state) => ({
              recipes: state.recipes.map((r) => {
                const updated = updatedRecipes.find((u) => u.id === r.id);
                return updated ? updated : r;
              }),
            }));
          } else {
            // --- Normal toggle mode: works for single id or array ---
            await Promise.all(
              idArray.map(async id => {
                const recipe = get().recipes.find(r => r.id === id);
                if (!recipe) return;

                const recipeRef = ref(database, `recipes/${id}/favorites`);
                const snapshot = await getSnapshot(recipeRef);
                const currentFavorites: string[] = snapshot.exists()
                  ? snapshot.val()
                  : [];
                const isAlreadyFavorite = currentFavorites.includes(userId);

                const updatedFavorites = isAlreadyFavorite
                  ? currentFavorites.filter(uid => uid !== userId)
                  : [...currentFavorites, userId];

                await update(ref(database, `recipes/${id}`), {
                  favorites: updatedFavorites,
                });

                set((state) => ({
                  recipes: state.recipes.map((r) =>
                    r.id === id ? { ...r, isFavorite: !isAlreadyFavorite } : r
                  )
                }));


                // optional notification per recipe
                get().addNotification({
                  title: isAlreadyFavorite ? 'Recipe Unfavorited' : 'Recipe Favorited',
                  message: `${recipe.title} has been ${isAlreadyFavorite ? 'removed from' : 'added to'
                    } your favorites`,
                  type: 'info',
                });
              })
            );
          }

          // get all favorites a make meal template

          const mealTemplate = get().recipes.filter(item => item.isFavorite);
          const mealTemplateRef = doc(db, 'mealTemplates', userId);

          await updateDoc(mealTemplateRef, {
            template: mealTemplate,
          });
          set({ mealTemplate, });

        } catch (error) {
          console.error('Error toggling favorites:', error);
          throw error;
        }
      },


      addNotification: (notification) => {
        const newNotification: Notification = {
          id: Math.random().toString(36).substring(2),
          date: new Date(),
          read: false,
          ...notification
        };

        set(state => ({
          notifications: [newNotification, ...state.notifications]
        }));
      },

      markNotificationAsRead: (id: string) => {
        set(state => ({
          notifications: state.notifications.map(notification =>
            notification.id === id ? { ...notification, read: true } : notification
          )
        }));
      },

      acceptTerms: async () => {
        const userId = get().user?.id;
        if (!userId) throw new Error('User ID is undefined');
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, { termsAccepted: true });
        let userInfo: any = get().user;
        set({
          user: {
            ...userInfo,
            termsAccepted: true
          }
        })
      },

      clearNotifications: () => {
        set({ notifications: [] });
      },

      cancelSubscription: async () => {
        set({ subscription: null });
      },

      updateSubscription: (subscription: 'free' | 'pro' | null) => {
        if (subscription) {
          set({ subscription });
        }
        get().addNotification({
          title: 'Subscription Updated',
          message: `Your subscription has been updated to ${subscription ? subscription : 'free'}`,
          type: 'success'
        });
      },

      updatePreferences: async (prefs: Partial<UserPreferences>) => {
        const userId = get().user?.id;
        if (!userId) return;

        try {
          const userPrefsRef = doc(db, 'userPreferences', userId);
          await setDoc(userPrefsRef, prefs, { merge: true });
          let userPreferences: UserPreferences = { ...get().userPreferences, ...prefs } as UserPreferences;
          if (userPreferences?.theme === "light") {
            set({ appLogo: "https://res.cloudinary.com/drive-cloud/image/upload/v1756090835/Asset_1-removebg-preview_cbbaej.png" });
          } else {
            set({ appLogo: "https://res.cloudinary.com/drive-cloud/image/upload/v1756090835/Asset_1-removebg-preview_cbbaej.png" });
          }
          set({ userPreferences });

          get().addNotification({
            title: 'Preferences Updated',
            message: 'Your preferences have been saved successfully',
            type: 'success'
          });
        } catch (error) {
          console.error('Error updating preferences:', error);
          throw error;
        }
      },

      updateMealTemplate: async (mealTemplate: Recipe[], mealPriority: string[], mealsPerWeek: number, mealPriorities: any) => {
        const userId = get().user?.id;
        try {
          if (!userId) {
            throw new Error('User ID is undefined');
          }
          const mealTemplateRef = doc(db, 'mealTemplates', userId);

          if (mealTemplate) {
            const ids = mealTemplate.map((meal) => meal.id);
            get().toggleFavorite(ids, { replace: true })
          }

          // console.log("Updating template", mealPriorities, mealPriority)

          await setDoc(mealTemplateRef, {
            template: mealTemplate,
            mealPriority,
            mealsPerWeek,
            mealPriorities
          }, { merge: true });
          set({ mealTemplate, mealPriority, mealsPerWeek, mealPriorities });
        } catch (err) {
          console.error('Error updating meal template:', err);
        }
      },

      fetchMealTemplate: async () => {
        const userId = get().user?.id;
        try {
          if (!userId) {
            throw new Error('User ID is undefined');
          }

          const mealTemplateRef = doc(db, 'mealTemplates', userId);
          const snapshot = await getDoc(mealTemplateRef);

          if (!snapshot.exists()) {
            console.warn('No meal template found for this user');
            return null;
          }

          const data = snapshot.data();

          // Update store with fetched values
          set({
            mealTemplate: data.template || [],
            mealPriority: data.mealPriority || [],
            mealsPerWeek: data.mealsPerWeek || 0,
            mealPriorities: data.mealPriorities || {},
            mealTime: data.mealTime || { breakfast: "07:00", lunch: "12:00", dinner: "18:00" }
          });

          return data;
        } catch (err) {
          console.error('Error fetching meal template:', err);
          return null;
        }
      },


      updateMealTime: async (mealTime: any) => {
        const userId = get().user?.id;
        try {
          if (!userId) {
            throw new Error('User ID is undefined');
          }
          const mealTemplateRef = doc(db, 'mealTemplates', userId);

          if (!mealTime) {
            return
          }

          // console.log("Updating template", mealPriorities, mealPriority)

          await setDoc(mealTemplateRef, {
            mealTime
          }, { merge: true });
          set({ mealTime });
        } catch (err) {
          console.error('Error updating meal time:', err);
        }
      },

      updateWeeklyPlan: async (weeklyPlan: WeeklyPlans) => {
        const userId = get().user?.id;
        if (!userId) return;
        try {
          const planRef = doc(db, 'weeklyPlans', userId);
          await setDoc(planRef, { ...weeklyPlan });
          set({ weeklyPlan });
        } catch (error) {
          console.error('Error updating weekly plan:', error);
          throw error;
        }
      },

      updateProfile: async (profile: User) => {
        const userId = get().user?.id;
        if (!userId) return;
        try {
          const userRef = doc(db, 'users', userId);
          await updateDoc(userRef, { ...profile });
          console.log('Profile updated:', profile);

          set(state => ({
            user: {
              ...state.user,
              ...profile
            }
          }) as Partial<StoreState>);

          get().addNotification({
            title: 'Profile Updated',
            message: 'Your profile has been updated successfully',
            type: 'success'
          });
        } catch (error) {
          console.error('Error updating profile:', error);
          throw error;
        }
      },

      updateLocation: (location: Location) => {
        set({ location });

        get().addNotification({
          title: 'Location Updated',
          message: 'Your location and currency preferences have been updated',
          type: 'info'
        });
      },

      setShowSignUp: (show) => set({ showSignUp: show }),

      addMealToPlan: async (date: Date, mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack', weekId: string, recipe: Meal[]) => {
        const userId = get().user?.id;
        if (!userId) return;

        try {
          const planRef = doc(db, 'weeklyPlans', userId);
          const planSnap = await getDoc(planRef);
          // const weekId = getWeekInfo();
          const currentPlan = get().weeklyPlan;
          const days = Array.from({ length: 7 }, (_, i) => {
            const date = new Date();
            date.setDate(date.getDate() - date.getDay() + i + 1);
            return {
              date,
              meals: {
                breakfast: null,
                lunch: null,
                dinner: null,
                snacks: []
              }
            };
          });
          const weekPlan = currentPlan[weekId] || days;

          const updatedWeekPlan = weekPlan.map(day => {
            console.log("Inspecting day:", day); // Log the whole day object
            let dayDate;
            try {
              dayDate = day?.date?.toDate();
            } catch (error) {
              dayDate = new Date(day?.date);
            }
            if (dayDate && date && dayDate.toDateString() === date.toDateString()) {

              const newMeal: Meal[] = recipe.map(item => ({
                id: item.id,
                title: item.title,
                type: mealType,
                image: item.image,
                calories: item.calories,
                recipeId: item.recipeId
              }));

              const mealKey = mealType === 'snack' ? 'snacks' : mealType;
              const existingMeals = day.meals[mealKey];

              let updatedMeals: Meal[] = [];

              if (Array.isArray(existingMeals)) {
                updatedMeals = [...existingMeals, ...newMeal];
              } else if (existingMeals) {
                updatedMeals = [existingMeals, ...newMeal];
              } else {
                updatedMeals = [...newMeal];
              }

              return {
                ...day,
                meals: {
                  ...day.meals,
                  [mealType]: updatedMeals
                }
              };
            }
            return day;
          });

          const updatedPlan = {
            ...currentPlan,
            [weekId]: updatedWeekPlan
          };

          console.log("Updated Plan: ", updatedPlan)

          const formattedPlanForFirestore = Object.fromEntries(
            Object.entries(updatedPlan).map(([key, days]) => [
              key,
              (days as any[]).map(day => {
                console.log("Inspecting day:", day); // Log the whole day object
                return { ...day, date: formatDate(day.date) };
              }),
            ])
          );

          if (!planSnap.exists()) {
            await setDoc(planRef, {
              ...formattedPlanForFirestore,
            }, { merge: true });
          } else {
            await updateDoc(planRef, {
              ...formattedPlanForFirestore,
            });
          }

          set({ weeklyPlan: updatedPlan });


          get().addNotification({
            title: 'Meal Added',
            message: `${recipe[0].title} + ${recipe.length - 1} has been added to your meal plan`,
            type: 'success'
          });
        } catch (error) {
          console.error('Error updating meal plan:', error);
          get().addNotification({
            title: 'Error',
            message: 'Failed to update meal plan',
            type: 'error'
          });
        }
      },

      updateUserData: async () => {
        const uid = get().user?.id;

        const authUser = auth.currentUser;
        if (!authUser || !uid) {
          get().logout();
          throw new Error("No user is currently signed in.");
        }
        const idToken = await authUser.getIdToken(true);
        const token = idToken;
        let user = await validateToken(token, uid);
        if (user) {
          set({ user });
          if (user.subscriptionHistory) {
            set({ subscriptionHistory: user.subscriptionHistory });
          }
        } else {
          get().logout();
          throw "validation error!";
        }
      },

      resendOTP: async () => {
        try {
          if (!auth.currentUser) {
            throw new Error("No user is currently authenticated");
          }

          const token = await auth.currentUser.getIdToken(true);
          const uid = get().user?.id;
          const baseUrl = import.meta.env.VITE_API_URL || 'https://app.ezyvibes.com';
          const response = await fetch(`${baseUrl}/api/resend-otp`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid, token })
          })

          const data = await response.json();
          console.log(data);
          if (response.status == 200) {
            return true;
          }
          return false;
        } catch (err) {
          return false;
        }
      },

      verifyOTP: async (otp: string) => {
        try {
          if (!auth.currentUser) {
            throw new Error("No user is currently authenticated");
          }

          const token = await auth.currentUser.getIdToken(true);
          const uid = get().user?.id;
          const baseUrl = import.meta.env.VITE_API_URL || 'https://app.ezyvibes.com';
          const response = await fetch(`${baseUrl}/api/verify-otp`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid, token, otp })
          })

          const data = await response.json()
          console.log('Verify Token Response:', data);
          if (data.user) {
            set({ user: data.user })
            if (data.user.verified) {
              get().addNotification({
                title: 'Success',
                message: 'Email verified successfully!',
                type: 'success'
              });
            }
          }
        } catch (err) {
          console.log(err);
        }
      },

      removeMealFromPlan: async (date: Date, mealType: string, weekId: string, mealId?: string) => {
        const userId = get().user?.id;
        if (!userId) return;

        try {
          const planRef = doc(db, "weeklyPlans", userId);
          const snap = await getDoc(planRef);

          // We only need the plan for this week
          const allPlans = snap.exists() ? snap.data() : {};
          const weekPlan: DayPlan[] = normalizeWeek(allPlans[weekId], weekId);

          // Update only the matching day in this week
          const updatedWeekPlan = weekPlan.map((day) => {
            const dayDate = formatDate(day?.date);
            if (dayDate?.toDateString() !== date.toDateString()) return day;

            const existing = day.meals[mealType as keyof typeof day.meals];
            return {
              ...day,
              meals: {
                ...day.meals,
                [mealType]:
                  mealId && Array.isArray(existing)
                    ? existing.filter((m) => m.id !== mealId)
                    : null,
              },
            };
          });

          // Firestore update for only this weekId
          const weekUpdate = {
            [weekId]: updatedWeekPlan.map((d) => ({
              ...d,
              date: formatDate(d.date), // convert Date to plain Date/Firestore-friendly value
            })),
          };

          console.log("[Removing: ]", weekUpdate)

          await setDoc(planRef, weekUpdate, { merge: true });

          // Update local state for current week only
          set((state) => ({
            weeklyPlan: {
              ...state.weeklyPlan,
              [weekId]: updatedWeekPlan,
            },
          }));

          get().addNotification({
            title: "Meal Removed",
            message: "Meal has been removed from your plan",
            type: "info",
          });
        } catch (err) {
          console.error("Error removing meal:", err);
          get().addNotification({
            title: "Error",
            message: "Failed to remove meal from plan",
            type: "error",
          });
        }
      },

      fetchRecipeById: (recipeId: string) => {
        return get().recipes.find((recipe) => recipe.id === recipeId);
      },

      estimateRecipeCost: async (recipeId: string, currency: string, amount: number) => {
        try {
          if (!auth.currentUser) {
            throw new Error("No user is currently authenticated");
          }

          const idToken = await auth.currentUser.getIdToken(true);
          const baseUrl = import.meta.env.VITE_API_URL || 'https://app.ezyvibes.com';
          const userId = get().user?.id;
          if (amount > 0) {
            const response = await fetch(`${baseUrl}/api/set-estimated-cost`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ recipeId, userId, amount, currency, idToken })
            })
            const data = await response.json()

            console.log('Estimated cost response:', data);
          } else {
            console.log('Cannot update cost <= 0');
          }
        } catch (error) {
          console.log("Error setting cost estimate: ", error);
        }
        await get().updateUserData();
      },

      setPlans: async (plans: Plan[]) => {
        set({ plans });
      },
      toggleSavedRecipe: (recipeId: string) => {
        set(state => ({
          savedRecipes: state.savedRecipes.includes(recipeId)
            ? state.savedRecipes.filter(id => id !== recipeId)
            : [...state.savedRecipes, recipeId]
        }));
      }
    }),
    {
      name: 'ezyvibes-hub-storage',
      partialize: (state) => ({
        user: state.user,
        subscription: state.subscription,
        location: state.location,
        subscriptionHistory: state.subscriptionHistory,
        mealTemplate: state.mealTemplate,
        mealPriority: state.mealPriority,
        mealPriorities: state.mealPriorities,
        mealsPerWeek: state.mealsPerWeek,
        appLogo: state.appLogo,
        weeklyPlan: state.weeklyPlan,
        userPreferences: state.userPreferences,
        recipes: state.recipes,
        showOnboarding: state.showOnboarding,
        lastFetch: state.lastFetch,
        mealTime: state.mealTime
        // weeklyPlan: state.weeklyPlan,
      })
    }
  )
);